import React, { useEffect, useState } from "react";
import { X, RefreshCw, Home } from "lucide-react";

/**
 * "Reinstall for the new icon" nudge.
 * Shown ONCE to users detected to be running the PWA in standalone mode
 * with a stale-icon marker. Bump ICON_VERSION when you ship a new icon
 * and anyone already installed will see it exactly once.
 */
const ICON_VERSION = "2026.02.05";
const STORAGE_KEY = "adn.iconVersion";

function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari sets navigator.standalone
  return !!window.navigator.standalone;
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export default function ReinstallForIcon() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!isStandalone()) return; // only nudge installed-PWA users
      const seen = localStorage.getItem(STORAGE_KEY);
      if (seen !== ICON_VERSION) {
        const t = setTimeout(() => setVisible(true), 2500);
        return () => clearTimeout(t);
      }
    } catch { /* localStorage unavailable */ }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, ICON_VERSION); } catch { /* noop */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-[calc(env(safe-area-inset-bottom)+96px)] md:bottom-6 left-4 right-4 md:left-6 md:right-auto md:max-w-sm z-[70]"
      data-testid="reinstall-nudge"
    >
      <div className="adn-card p-4 border border-primary/40 shadow-xl bg-[#0a1226]/95 backdrop-blur">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="reinstall-nudge-close"
          className="absolute top-2 right-2 p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10"
        >
          <X size={14} />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <RefreshCw size={18} className="text-primary shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-sm">New app icon available</div>
            <p className="text-xs text-white/70 mt-1 leading-relaxed">
              We refreshed the FanFest icon. To see it on your home screen, remove the current tile and add FanFest again.
            </p>
            {isIOS() ? (
              <p className="text-[11px] text-white/50 mt-2 leading-relaxed">
                <b className="text-white/80">iPhone:</b> long-press the FanFest icon → <i>Remove App</i> → open Safari → tap Share → <i>Add to Home Screen</i>.
              </p>
            ) : (
              <p className="text-[11px] text-white/50 mt-2 leading-relaxed">
                <b className="text-white/80">Android:</b> long-press the FanFest icon → <i>Uninstall</i>, then reopen the site in Chrome → menu → <i>Install app</i>.
              </p>
            )}
            <button
              type="button"
              onClick={dismiss}
              data-testid="reinstall-nudge-cta"
              className="mt-3 text-xs font-semibold px-3 py-1.5 rounded bg-primary text-black hover:brightness-110 inline-flex items-center gap-1.5"
            >
              <Home size={12} /> Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
